import React, { useEffect, useState } from 'react';
import {
	pushSupported,
	pushPermission,
	iosNeedsInstall,
	enablePushNotifications,
} from '../../services/pushNotifications';

/**
 * Slim opt-in banner for PWA task notifications. Self-hiding:
 *  - renders nothing when the browser can't do Web Push, when permission is
 *    already granted, or when permission was previously denied (can't reprompt);
 *  - shows a one-tap "Turn on" prompt otherwise.
 *
 * The permission request runs from the button's click (a user gesture — required
 * by iOS). On iOS the PWA must be launched from the Home Screen; if it isn't, we
 * show an "Add to Home Screen" hint instead of failing silently.
 *
 * Drop it into a portal home (TeacherDashboard / EmployeeDashboard) — it manages
 * its own visibility, so no parent state is needed.
 */
function NotificationOptIn() {
	const [ready, setReady] = useState(false); // supported + still actionable
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState('');
	const [done, setDone] = useState(false);

	useEffect(() => {
		let alive = true;
		(async () => {
			const supported = await pushSupported();
			if (!alive) return;
			// Only surface when we can still act: supported AND not already granted
			// AND not hard-denied.
			const perm = pushPermission();
			setReady(supported && perm !== 'granted' && perm !== 'denied');
		})();
		return () => {
			alive = false;
		};
	}, []);

	if (!ready || done) return null;

	const onEnable = async () => {
		setBusy(true);
		setMessage('');
		try {
			await enablePushNotifications();
			setDone(true);
		} catch (err) {
			const code = err?.message;
			if (code === 'ios-needs-install') {
				setMessage(
					'On iPhone, first add the portal to your Home Screen (Share → Add to Home Screen), open it from there, then turn on notifications.'
				);
			} else if (code === 'denied') {
				setMessage(
					'Notifications are blocked. Enable them for this app in your device settings, then try again.'
				);
			} else if (code === 'not-configured') {
				setMessage('Notifications aren’t configured yet. Check back soon.');
			} else if (code === 'not-signed-in') {
				setMessage('Please sign in again, then try.');
			} else {
				setMessage('Couldn’t turn on notifications. Please try again.');
			}
		} finally {
			setBusy(false);
		}
	};

	const showInstallHint = iosNeedsInstall();

	return (
		<div
			className='cyv-card'
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: '0.75rem',
				padding: '0.85rem 1rem',
				margin: '0 0 1rem',
			}}>
			<span
				aria-hidden='true'
				style={{ fontSize: '1.15rem', lineHeight: 1, opacity: 0.85 }}>
				<i className='fa-sharp fa-solid fa-bell' />
			</span>
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={{ fontWeight: 600 }}>Task notifications</div>
				<div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
					{message ||
						(showInstallHint
							? 'Add the portal to your Home Screen to get push alerts when a task is assigned or due.'
							: 'Get a push when a task is assigned to you or due today.')}
				</div>
			</div>
			{!showInstallHint && (
				<button
					type='button'
					onClick={onEnable}
					disabled={busy}
					style={{
						whiteSpace: 'nowrap',
						border: 'none',
						borderRadius: '0.5rem',
						padding: '0.5rem 0.9rem',
						fontWeight: 600,
						cursor: busy ? 'default' : 'pointer',
						color: '#fff',
						background: '#384971',
						opacity: busy ? 0.7 : 1,
					}}>
					{busy ? 'Turning on…' : 'Turn on'}
				</button>
			)}
		</div>
	);
}

export default NotificationOptIn;
